import { Button, Theme, Tooltip, Typography } from "@mui/material";
import { FC, MouseEventHandler, useRef } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { FormattedMessage } from "react-intl";
import { useTooltipText } from "../../../../../Hooks/useTooltipText";

interface ViewItemProps {
    selected: boolean;
    isGlobal?: boolean;
    selectElement: MouseEventHandler<HTMLButtonElement>;
    elementName: string;
    editHandler: MouseEventHandler<SVGSVGElement>;
    copyHandler: MouseEventHandler<SVGSVGElement>;
    deleteHandler: MouseEventHandler<SVGSVGElement>;
}

const ViewItem: FC<ViewItemProps> = ({ selected, selectElement, elementName, editHandler, copyHandler, deleteHandler, isGlobal = false }) => {
    const ref = useRef<HTMLHeadingElement>(null);
    const tooltipText = useTooltipText(ref, elementName);

    const getBackgroundColor = (theme: Theme) => {
        if (!selected) {
            return "unset";
        }

        if (isGlobal) {
            return theme.palette.cobaltBlue.light;
        }

        return theme.palette.teal.light;
    };

    return (
        <div>
            <Button
                onClick={selectElement}
                sx={{
                    lineHeight: 0,
                    width: "100%",
                    alignItems: "center",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    minHeight: "46px",
                    "& .MuiSvgIcon-root": { cursor: "pointer", opacity: 0, transition: "all 250ms" },
                    ...(isGlobal
                        ? {
                              "& .MuiTypography-root": { color: (theme) => theme.palette.cobaltBlue?.main },
                          }
                        : {}),
                    ":hover": {
                        backgroundColor: (theme) => (isGlobal ? theme.palette.cobaltBlue?.light : theme.palette.teal?.light),
                        "& .MuiSvgIcon-root": { opacity: 0.5, ":hover": { opacity: 1 } },
                    },
                    "&.MuiButton-root": {
                        color: (theme) => theme.palette.teal?.main,
                    },
                    backgroundColor: getBackgroundColor,
                }}
            >
                <Tooltip title={tooltipText} placement="top" arrow>
                    <Typography
                        ref={ref}
                        component="h6"
                        variant="body1"
                        sx={{
                            textAlign: "left",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {elementName}
                    </Typography>
                </Tooltip>
                <div style={{ display: "flex", flexDirection: "row" }}>
                    {!isGlobal && (
                        <Tooltip title={<FormattedMessage id="common.button-label.edit" defaultMessage="Edit" />} arrow>
                            <EditIcon
                                htmlColor="black"
                                sx={{ padding: "5px" }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    editHandler(e);
                                }}
                            />
                        </Tooltip>
                    )}
                    <Tooltip title={<FormattedMessage id="common.button-label.copy" defaultMessage="Copy" />} arrow>
                        <ContentCopyIcon
                            htmlColor="black"
                            sx={{ padding: "5px" }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                copyHandler(e);
                            }}
                        />
                    </Tooltip>
                    {!isGlobal && (
                        <Tooltip title={<FormattedMessage id="common.button-label.delete" defaultMessage="Delete" />} arrow>
                            <DeleteIcon
                                htmlColor="black"
                                sx={{ padding: "5px" }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    deleteHandler(e);
                                }}
                            />
                        </Tooltip>
                    )}
                </div>
            </Button>
        </div>
    );
};

export default ViewItem;
